// Page publique Mécénat — formulaire de don avec reçu fiscal automatique (art. 200 / 238 bis CGI)
import { useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";
import BookingClosedBanner, { useBookingConfig } from "@/components/BookingClosedBanner";
import SumUpBadge from "@/components/payments/SumUpBadge";

const SUGGESTED_AMOUNTS = [20, 50, 100, 250, 500];

export default function Mecenat() {
  const { locale } = useI18n();
  const config = useBookingConfig();
  const bookingOpen = !config || config.booking_open;
  const isFr = locale === "fr";

  const [donorType, setDonorType] = useState("particulier");
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    civility: "Mme", name: "", siret: "", email: "", phone: "",
    address: "", postal_code: "", city: "", country: "France",
    message: "", anonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;
  const isCompany = donorType === "entreprise";
  const taxRate = isCompany ? 0.60 : 0.66;
  const realCost = finalAmount && !isNaN(finalAmount) ? (finalAmount * (1 - taxRate)).toFixed(2) : "—";
  const article = isCompany ? "238 bis" : "200";

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!finalAmount || finalAmount < 5) {
      setError(isFr ? "Le montant minimum est de 5 €." : "Minimum donation is €5.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/donations/mecenat", {
        donor_type: donorType,
        civility: isCompany ? null : form.civility,
        name: form.name,
        siret: isCompany ? form.siret || null : null,
        email: form.email,
        phone: form.phone || null,
        address: form.address,
        postal_code: form.postal_code,
        city: form.city,
        country: form.country,
        amount: finalAmount,
        anonymous: form.anonymous,
        message: form.message || null,
        locale,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        setSuccess(data);
      }
    } catch (ex) {
      setError(ex?.response?.data?.detail || (isFr ? "Erreur lors de l'envoi." : "Submission error."));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="container-prieure max-w-2xl py-16">
        <div data-testid="mecenat-success" className="bg-white border p-10 text-center space-y-4"
             style={{ borderColor: "var(--gold)", borderLeftWidth: 3 }}>
          <Ornament />
          <h1 className="font-display text-3xl">{isFr ? "Merci de tout cœur" : "Heartfelt thanks"}</h1>
          <p className="font-body text-base" style={{ color: "var(--ink-2)" }}>{success.message}</p>
          <p className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
            {isFr ? "Référence : " : "Reference: "}{success.session_id}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mecenat-page" className="pb-20">
      {/* HERO */}
      <section className="relative" style={{ background: "var(--vellum)" }}>
        <div className="container-prieure max-w-4xl pt-20 pb-12 text-center">
          <div className="font-caps text-[0.75rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.3em" }}>
            {isFr ? "SOUTENIR LE PRIEURÉ" : "SUPPORT THE PRIORY"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-4">{isFr ? "Mécénat" : "Patronage"}</h1>
          <Ornament />
          <p className="font-body text-lg mt-6 max-w-2xl mx-auto" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Aidez-nous à préserver mille ans de mémoire vivante, à entretenir la chapelle et à transmettre l'esprit du lieu."
              : "Help us preserve a thousand years of living memory, maintain the chapel and pass on the spirit of the place."}
          </p>
        </div>
      </section>

      <div className="container-prieure max-w-5xl grid md:grid-cols-5 gap-10 mt-10">
        {/* LEFT: pourquoi donner */}
        <aside className="md:col-span-2 space-y-6">
          <div>
            <div className="font-caps text-[0.65rem] mb-2" style={{ color: "var(--cinnabar)", letterSpacing: "0.22em" }}>
              {isFr ? "AVANTAGE FISCAL" : "TAX BENEFIT"}
            </div>
            <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
              {isFr ? (
                <>
                  Votre don ouvre droit à une <strong>réduction d'impôt de 66 %</strong> pour les particuliers
                  (art. 200 CGI), <strong>60 % pour les entreprises</strong> (art. 238 bis CGI).
                </>
              ) : (
                <>
                  Your donation entitles you to a <strong>66 % tax reduction</strong> for individuals
                  (art. 200 CGI), <strong>60 % for companies</strong> (art. 238 bis CGI).
                </>
              )}
            </p>
          </div>

          <div className="border-l-2 pl-4" style={{ borderColor: "var(--gold)" }}>
            <div className="font-caps text-[0.65rem] mb-1" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>
              {isFr ? "EXEMPLE" : "EXAMPLE"}
            </div>
            <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
              {isFr ? "Un don de " : "A donation of "}<strong>100 €</strong>
              {isFr ? " ne vous coûte réellement que " : " only costs you "}<strong>34 €</strong>
              {isFr ? " après réduction d'impôt." : " after tax reduction."}
            </p>
          </div>

          <div>
            <div className="font-caps text-[0.65rem] mb-2" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
              {isFr ? "VOTRE SOUTIEN PERMET" : "YOUR SUPPORT FUNDS"}
            </div>
            <ul className="font-body text-sm space-y-2" style={{ color: "var(--ink-2)" }}>
              <li>{isFr ? "— L'entretien de la chapelle et de ses abords" : "— Upkeep of the chapel and its surroundings"}</li>
              <li>{isFr ? "— La valorisation du patrimoine spirituel et artistique" : "— Promotion of the spiritual and artistic heritage"}</li>
              <li>{isFr ? "— Les rencontres, ostensions et études scientifiques" : "— Gatherings, ostensions and scientific studies"}</li>
              <li>{isFr ? "— La préparation des Fêtes de saint Léonard 2027" : "— Preparation of the 2027 Feasts of Saint Léonard"}</li>
            </ul>
          </div>
        </aside>

        {/* RIGHT: form */}
        <div className="md:col-span-3">
          {!bookingOpen && <BookingClosedBanner />}
          <form onSubmit={submit} data-testid="mecenat-form" className="bg-white border p-8 space-y-6" style={{ borderColor: "var(--border)" }}>
            {/* Donor type toggle */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "particulier", label: isFr ? "Particulier" : "Individual", rate: "66 %" },
                { value: "entreprise", label: isFr ? "Entreprise" : "Company", rate: "60 %" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`donor-type-${opt.value}`}
                  onClick={() => setDonorType(opt.value)}
                  className="border p-3 font-caps text-xs transition-colors"
                  style={{
                    borderColor: donorType === opt.value ? "var(--gold)" : "var(--border)",
                    background: donorType === opt.value ? "rgba(201,184,118,0.1)" : "transparent",
                    color: donorType === opt.value ? "var(--ink)" : "var(--ink-2)",
                    letterSpacing: "0.18em",
                    borderWidth: donorType === opt.value ? 2 : 1,
                  }}
                >
                  {opt.label}
                  <div className="font-body text-[10px] mt-1 normal-case" style={{ color: "var(--muted)", letterSpacing: 0 }}>
                    {isFr ? `Réduction ${opt.rate}` : `${opt.rate} reduction`}
                  </div>
                </button>
              ))}
            </div>

            {/* Amount */}
            <div>
              <div className="label-caps mb-2">{isFr ? "MONTANT DE VOTRE DON" : "DONATION AMOUNT"}</div>
              <div className="grid grid-cols-5 gap-1 mb-2">
                {SUGGESTED_AMOUNTS.map((a) => (
                  <button
                    key={a} type="button"
                    data-testid={`amount-${a}`}
                    onClick={() => { setAmount(a); setCustomAmount(""); }}
                    className="border py-2.5 font-display text-lg transition-colors"
                    style={{
                      borderColor: amount === a && !customAmount ? "var(--gold)" : "var(--border)",
                      background: amount === a && !customAmount ? "rgba(201,184,118,0.1)" : "transparent",
                      color: "var(--ink)",
                      borderWidth: amount === a && !customAmount ? 2 : 1,
                    }}
                  >{a} €</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-body text-sm" style={{ color: "var(--muted)" }}>
                  {isFr ? "ou montant libre :" : "or custom amount:"}
                </span>
                <input
                  data-testid="amount-custom"
                  type="number" min="5" step="1"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="input-classic" style={{ flex: 1, maxWidth: 140 }}
                  placeholder={isFr ? "ex. 75" : "e.g. 75"}
                />
                <span className="font-body text-sm">€</span>
              </div>
            </div>

            {/* Real cost preview */}
            {finalAmount && finalAmount >= 5 && (
              <div data-testid="real-cost" className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--moss)", background: "rgba(132,159,114,0.06)" }}>
                <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>
                  {isFr ? "Après réduction d'impôt, votre don de " : "After tax reduction, your donation of "}
                  <strong>{finalAmount} €</strong>
                  {isFr ? " ne vous coûtera réellement que " : " will really cost you only "}
                  <strong style={{ color: "var(--moss)" }}>{realCost} €</strong>.
                </p>
              </div>
            )}

            {/* Donor info */}
            <div className="space-y-3">
              <div className="label-caps">{isFr ? "VOS COORDONNÉES" : "YOUR DETAILS"}</div>
              {!isCompany && (
                <div className="grid grid-cols-3 gap-2">
                  {["M.", "Mme", "Mlle"].map((c) => (
                    <button key={c} type="button"
                            data-testid={`civility-${c}`}
                            onClick={() => setForm((s) => ({ ...s, civility: c }))}
                            className="border py-2 font-body text-sm"
                            style={{
                              borderColor: form.civility === c ? "var(--gold)" : "var(--border)",
                              background: form.civility === c ? "rgba(201,184,118,0.1)" : "transparent",
                            }}>{c}</button>
                  ))}
                </div>
              )}
              <input data-testid="donor-name" required value={form.name} onChange={setField("name")}
                     className="input-classic" style={{ width: "100%" }}
                     placeholder={isCompany ? (isFr ? "Raison sociale *" : "Company name *") : (isFr ? "Nom et prénom *" : "Full name *")} />
              {isCompany && (
                <input data-testid="donor-siret" value={form.siret} onChange={setField("siret")}
                       className="input-classic" style={{ width: "100%" }} placeholder="SIRET (optionnel)" />
              )}
              <input data-testid="donor-email" required type="email" value={form.email} onChange={setField("email")}
                     className="input-classic" style={{ width: "100%" }} placeholder={isFr ? "Email *" : "Email *"} />
              <input data-testid="donor-phone" type="tel" value={form.phone} onChange={setField("phone")}
                     className="input-classic" style={{ width: "100%" }} placeholder={isFr ? "Téléphone (optionnel)" : "Phone (optional)"} />
              <input data-testid="donor-address" required value={form.address} onChange={setField("address")}
                     className="input-classic" style={{ width: "100%" }}
                     placeholder={isFr ? "Adresse complète *" : "Full address *"} />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="donor-postal" required value={form.postal_code} onChange={setField("postal_code")}
                       className="input-classic" placeholder={isFr ? "Code postal *" : "Postal code *"} />
                <input data-testid="donor-city" required value={form.city} onChange={setField("city")}
                       className="input-classic" placeholder={isFr ? "Ville *" : "City *"} />
              </div>
              <input data-testid="donor-country" required value={form.country} onChange={setField("country")}
                     className="input-classic" style={{ width: "100%" }} placeholder={isFr ? "Pays *" : "Country *"} />
              <textarea data-testid="donor-message" value={form.message} onChange={setField("message")}
                        className="input-classic" rows={3} style={{ width: "100%" }}
                        placeholder={isFr ? "Un message pour l'association (facultatif)" : "A message (optional)"} />
              <label className="flex items-center gap-2 font-body text-sm" style={{ color: "var(--ink-2)" }}>
                <input data-testid="donor-anonymous" type="checkbox"
                       checked={form.anonymous}
                       onChange={(e) => setForm((s) => ({ ...s, anonymous: e.target.checked }))} />
                {isFr ? "Je souhaite rester anonyme (mon nom n'apparaîtra pas sur la liste des soutiens)" : "I wish to remain anonymous"}
              </label>
            </div>

            {error && <p className="font-body text-sm" style={{ color: "var(--cinnabar)" }}>{error}</p>}

            <button type="submit" data-testid="mecenat-submit" disabled={submitting}
                    className="btn btn-primary w-full" style={{ padding: "0.9rem 1.5rem" }}>
              {submitting
                ? (isFr ? "Envoi en cours…" : "Submitting…")
                : (isFr ? `Faire un don de ${finalAmount || 0} €` : `Donate €${finalAmount || 0}`)}
            </button>

            <p className="font-body text-xs italic text-center" style={{ color: "var(--muted)" }}>
              {isFr
                ? `Vous recevrez automatiquement votre reçu fiscal (art. ${article} CGI) par email après confirmation du paiement.`
                : `You will automatically receive your tax receipt (art. ${article} CGI) by email after payment confirmation.`}
            </p>

            <SumUpBadge />
          </form>
        </div>
      </div>
    </div>
  );
}
